import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styles from './Welcome.module.css';

const Welcome = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [currentStat, setCurrentStat] = useState(0);
  const [animateStats, setAnimateStats] = useState(false);
  
  const stats = [
    { number: 15000, label: 'Active Users', suffix: '+' },
    { number: 500000, label: 'Days Tracked', suffix: '+' },
    { number: 98, label: 'Success Rate', suffix: '%' }
  ];

  useEffect(() => {
    setIsVisible(true);
    
    // Animate stats counter
    const timer = setTimeout(() => {
      setAnimateStats(true);
    }, 1500);
    
    // Cycle through stats
    const interval = setInterval(() => {
      setCurrentStat(prev => (prev + 1) % stats.length);
    }, 3000);
    
    return () => {
      clearTimeout(timer);
      clearInterval(interval);
    };
  }, []);

  const AnimatedCounter = ({ end, duration = 2000 }) => {
    const [count, setCount] = useState(0);
    
    useEffect(() => {
      if (!animateStats) return;
      
      let startTime;
      const animate = (currentTime) => {
        if (!startTime) startTime = currentTime;
        const progress = Math.min((currentTime - startTime) / duration, 1);
        setCount(Math.floor(progress * end));
        
        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };
      requestAnimationFrame(animate);
    }, [animateStats, end, duration]);
    
    return count;
  };

  return (
    <div className={styles.container}>
      {/* Animated Background Elements */}
      <div className={styles.floatingElements}>
        {[...Array(10)].map((_, i) => (
          <div key={i} className={`${styles.floatingElement} ${styles[`float${i + 1}`]}`}></div>
        ))}
      </div>
      
      <div className={`${styles.hero} ${isVisible ? styles.fadeInUp : ''}`}>
        <div className={styles.heroContent}>
          <h1 className={`${styles.title} ${isVisible ? styles.slideInLeft : ''}`}>
            <span className={styles.titleMain}>TrackMyFap</span>
            <span className={styles.titleGlow}>Transform Your Life</span>
          </h1>
          
          <p className={`${styles.subtitle} ${isVisible ? styles.slideInRight : ''}`}>
            🎯 Break Free • 💪 Build Discipline • 🌟 Achieve Greatness
          </p>
          
          <p className={`${styles.description} ${isVisible ? styles.fadeIn : ''}`}>
            Join the community that's redefining self-improvement. Track your progress, 
            earn achievements, and build lasting habits that transform your life.
          </p>
          
          {/* Animated Stats */}
          <div className={`${styles.statsContainer} ${animateStats ? styles.statsVisible : ''}`}>
            <div className={styles.statItem}>
              <span className={styles.statNumber}>
                <AnimatedCounter end={stats[currentStat].number} />
                {stats[currentStat].suffix}
              </span>
              <span className={styles.statLabel}>{stats[currentStat].label}</span>
            </div>
          </div>
        </div>
      </div>
      
      <div className={`${styles.features} ${isVisible ? styles.staggeredAnimation : ''}`}>
        <div className={`${styles.feature} ${styles.featureDelay1}`}>
          <div className={styles.featureIcon}>
            <span className={styles.icon}>📈</span>
            <div className={styles.iconGlow}></div>
          </div>
          <h3>Smart Progress Tracking</h3>
          <p>Advanced analytics with visual insights, streak monitoring, and personalized recommendations to keep you on track.</p>
          <div className={styles.featureFooter}>
            <span className={styles.featureBadge}>AI-Powered</span>
          </div>
        </div>
        
        <div className={`${styles.feature} ${styles.featureDelay2}`}>
          <div className={styles.featureIcon}>
            <span className={styles.icon}>🏆</span>
            <div className={styles.iconGlow}></div>
          </div>
          <h3>Achievement System</h3>
          <p>Unlock exclusive badges, celebrate milestones, and showcase your dedication with our gamified reward system.</p>
          <div className={styles.featureFooter}>
            <span className={styles.featureBadge}>25+ Badges</span>
          </div>
        </div>
        
        <div className={`${styles.feature} ${styles.featureDelay3}`}>
          <div className={styles.featureIcon}>
            <span className={styles.icon}>👥</span>
            <div className={styles.iconGlow}></div>
          </div>
          <h3>Supportive Community</h3>
          <p>Connect with motivated individuals, share victories, get support during challenges, and inspire others.</p>
          <div className={styles.featureFooter}>
            <span className={styles.featureBadge}>15K+ Members</span>
          </div>
        </div>
        
        <div className={`${styles.feature} ${styles.featureDelay4}`}>
          <div className={styles.featureIcon}>
            <span className={styles.icon}>🎯</span>
            <div className={styles.iconGlow}></div>
          </div>
          <h3>Custom Goal Setting</h3>
          <p>Create personalized challenges, set meaningful targets, and track progress with detailed insights and reminders.</p>
          <div className={styles.featureFooter}>
            <span className={styles.featureBadge}>Fully Customizable</span>
          </div>
        </div>
      </div>
      
      <div className={`${styles.cta} ${isVisible ? styles.ctaVisible : ''}`}>
        <div className={styles.ctaContent}>
          <h2 className={styles.ctaTitle}>Ready to Transform Your Life?</h2>
          <p className={styles.ctaSubtitle}>Join thousands who've already started their journey to success</p>
          
          <div className={styles.buttonContainer}>
            <Link to="/login" className={`${styles.button} ${styles.primaryButton}`}>
              <span className={styles.buttonText}>Start Free Today</span>
              <span className={styles.buttonIcon}>🚀</span>
              <div className={styles.buttonGlow}></div>
            </Link>
            
            <div className={styles.trustSignals}>
              <span className={styles.trustItem}>✅ No Credit Card Required</span>
              <span className={styles.trustItem}>✅ Join in Under 30 Seconds</span>
              <span className={styles.trustItem}>✅ 100% Privacy Protected</span>
            </div>
          </div>
        </div>
        
        <div className={styles.urgencyBanner}>
          <span className={styles.urgencyText}>🔥 Over 1,000 people joined this week!</span>
        </div>
      </div>
      
      <footer className={`${styles.footer} ${isVisible ? styles.fadeIn : ''}`}>
        <p>&copy; 2024 TrackMyFap • Empowering 15,000+ people worldwide</p>
      </footer>
    </div>
  );
};

export default Welcome;